import React from 'react';
import { Sparkles, Shirt } from 'lucide-react';
import type { LookCardData, LookSlot } from '../../utils/lookBlocks';
import type { ProductCardData } from '../../utils/productBlocks';
import ProductCard from './ProductCard';

interface LookCardProps {
  data: LookCardData;
  brandColor: string;
  isDark: boolean;
  /** Virtual Try-On (Phase 3) — when provided, render a "Try on this look"
   *  button. Passed ONLY from the chat widget render path (Widget.tsx) when
   *  VTO is enabled; absent in the voice panel, so the button never appears
   *  there. The per-slot ProductCards never get a try-on button (look-level
   *  only). */
  onTryOnLook?: () => void;
}

/** Title-case a slot role like "top" / "outer_layer" for the label. */
function formatRole(role: string): string {
  if (!role) return '';
  return role
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A slot is renderable only if its product carries at least a title OR a
 * price. ProductCard already degrades gracefully when `image`/`variants`/
 * `checkout_url` are absent (each is conditionally rendered), but a product
 * with neither title nor price would paint a blank box — so we drop those.
 * Returns a string-coerced product so a numeric `price` (off-contract — the
 * contract is a preformatted string) still renders instead of breaking React.
 */
function usableProduct(slot: LookSlot): ProductCardData | null {
  const p = slot && (slot.product as Partial<ProductCardData> | undefined);
  if (!p || typeof p !== 'object') return null;
  const title = p.title != null ? String(p.title) : '';
  const price = p.price != null ? String(p.price) : '';
  if (!title.trim() && !price.trim()) return null;
  return {
    product_id: p.product_id != null ? String(p.product_id) : '',
    title,
    price,
    image: p.image != null ? String(p.image) : undefined,
    variants: p.variants != null ? String(p.variants) : undefined,
    handle: p.handle != null ? String(p.handle) : undefined,
    checkout_url: p.checkout_url != null ? String(p.checkout_url) : undefined,
  };
}

const LookCard: React.FC<LookCardProps> = ({ data, brandColor, isDark, onTryOnLook }) => {
  const cardBg = isDark ? '#1f2937' : '#ffffff';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const dividerColor = isDark ? '#374151' : '#e5e7eb';
  const titleColor = isDark ? '#ffffff' : '#111827';
  const introColor = isDark ? '#e5e7eb' : '#374151';
  const labelColor = isDark ? '#9ca3af' : '#6b7280';
  const whyColor = isDark ? '#cbd5e1' : '#4b5563';
  const rolePillBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(17,24,39,0.05)';

  // Keep a slot if it yields a usable product OR carries standalone copy
  // (role / why) worth showing. Slots that are entirely empty are dropped
  // so the look never renders a blank product row.
  const rawSlots = Array.isArray(data.slots) ? data.slots : [];
  const slots = rawSlots
    .map((slot) => ({ slot, product: usableProduct(slot) }))
    .filter(({ slot, product }) => product || (slot && (slot.why?.trim() || slot.role?.trim())));

  const headerTitle = data.title && data.title.trim() ? data.title : 'Your look';

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${brandColor}`,
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '8px 0',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Sparkles size={15} color={brandColor} style={{ flexShrink: 0 }} />
          <div style={{ fontSize: '14px', fontWeight: 700, color: titleColor, lineHeight: '1.3' }}>
            {headerTitle}
          </div>
        </div>
        {data.intro && data.intro.trim() && (
          <div style={{ fontSize: '12.5px', color: introColor, lineHeight: '1.45' }}>
            {data.intro}
          </div>
        )}
      </div>

      {/* Slots */}
      {slots.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {slots.map(({ slot, product }, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {slot.role && formatRole(slot.role) && (
                <span
                  style={{
                    alignSelf: 'flex-start',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: labelColor,
                    background: rolePillBg,
                    padding: '2px 8px',
                    borderRadius: '999px',
                  }}
                >
                  {formatRole(slot.role)}
                </span>
              )}
              {product && (
                <ProductCard
                  {...product}
                  brandColor={brandColor}
                  isDark={isDark}
                />
              )}
              {slot.why && slot.why.trim() && (
                <div
                  style={{
                    fontSize: '11.5px',
                    color: whyColor,
                    lineHeight: '1.4',
                    fontStyle: 'italic',
                    padding: '0 2px',
                  }}
                >
                  {slot.why}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Virtual Try-On (Phase 3) — chat-only "Try on this look"; rendered
          when Widget.tsx passes onTryOnLook (VTO enabled). Only shown when the
          look has at least one usable product to try on. */}
      {onTryOnLook && slots.some(({ product }) => product) && (
        <button
          type="button"
          onClick={onTryOnLook}
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: brandColor,
            background: 'transparent',
            border: `1px solid ${brandColor}`,
            padding: '6px 0',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: '100%',
          }}
        >
          <Shirt size={14} style={{ flexShrink: 0 }} />
          Try on this look
        </button>
      )}

      {/* Outro */}
      {data.outro && data.outro.trim() && (
        <div
          style={{
            borderTop: `1px solid ${dividerColor}`,
            paddingTop: '8px',
            fontSize: '12.5px',
            color: introColor,
            lineHeight: '1.45',
          }}
        >
          {data.outro}
        </div>
      )}
    </div>
  );
};

export default LookCard;
